import { t } from "@lingui/macro";
import {
  ArrowRightIcon,
  FileArrowUpIcon,
  FilePlusIcon,
  LinkedinLogoIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

import type { StartMethod } from "../types";

type Props = {
  value: StartMethod;
  onChange: (value: StartMethod) => void;
  onContinue: () => void;
};

type StartChoice = {
  value: StartMethod;
  title: string;
  subtitle?: string;
  recommended?: boolean;
  icon: typeof FilePlusIcon;
  iconClassName: string;
};

export const StartMethodStep = ({ value, onChange, onContinue }: Props) => {
  const choices: StartChoice[] = [
    {
      value: "scratch",
      title: "Créer mon CV pas à pas",
      recommended: true,
      icon: FilePlusIcon,
      iconClassName: "border-emerald-300 bg-emerald-50 text-emerald-600",
    },
    {
      value: "document",
      title: "Importer un CV existant",
      subtitle: t`PDF (.pdf), Word (.docx) ou image (.jpg, .jpeg, .png, .webp)`,
      icon: FileArrowUpIcon,
      iconClassName: "border-violet-200 bg-violet-100 text-violet-700",
    },
    {
      value: "linkedin",
      title: "Importer depuis LinkedIn",
      icon: LinkedinLogoIcon,
      iconClassName: "border-blue-200 bg-blue-50 text-blue-600",
    },
  ];

  return (
    <section
      className={cn(
        "mx-auto flex h-full min-h-0 w-full max-w-2xl",
        "flex-col justify-center overflow-hidden",
      )}
    >
      <header className="shrink-0 text-center">
        <h1
          id="create-resume-title"
          className={cn(
            "mx-auto max-w-xl text-2xl font-bold",
            "leading-tight tracking-tight text-indigo-950",
            "sm:text-3xl lg:text-[34px]",
          )}
        >
          Comment souhaitez-vous commencer&nbsp;?
        </h1>

        <p
          className={cn(
            "mx-auto mt-2 max-w-lg",
            "text-xs leading-5 text-indigo-950/75",
            "sm:text-sm sm:leading-6",
          )}
        >
          Choisissez la méthode qui vous convient.
          <span className="block">Vous pourrez tout modifier ensuite.</span>
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label="Méthode de création"
        className={cn("mt-4 shrink-0 space-y-2.5", "sm:mt-5 sm:space-y-3")}
      >
        {choices.map((choice) => {
          const IconComponent = choice.icon;

          const active = choice.value === value;

          return (
            <button
              key={choice.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => {
                onChange(choice.value);
              }}
              className={cn(
                "group flex min-h-[64px] w-full items-center gap-3",
                "rounded-xl border-2 bg-white",
                "px-3 text-left",
                "transition-all duration-200",
                "hover:-translate-y-0.5 hover:border-indigo-200",
                "hover:shadow-[0_7px_18px_rgba(79,70,229,0.08)]",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                "sm:min-h-[70px] sm:gap-4 sm:px-4",
                active
                  ? "border-emerald-400 bg-emerald-50/35 shadow-[0_7px_20px_rgba(16,185,129,0.09)]"
                  : "border-slate-200",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "size-[18px] shrink-0 rounded-full border-2 bg-white",
                  "transition-all duration-200",
                  active
                    ? "border-[5px] border-emerald-500"
                    : "border-indigo-200 group-hover:border-indigo-300",
                )}
              />

              <span
                className={cn(
                  "grid size-10 shrink-0 place-items-center",
                  "rounded-xl border",
                  "sm:size-11",
                  choice.iconClassName,
                )}
              >
                <IconComponent aria-hidden size={22} weight="duotone" className="sm:size-6" />
              </span>

              <span className="min-w-0 flex-1">
                <span className={cn("block text-xs font-bold text-indigo-950", "sm:text-sm")}>
                  {choice.title}
                </span>

                {choice.subtitle && (
                  <span className={cn("mt-0.5 block text-[10px] text-slate-500", "sm:text-xs")}>
                    {choice.subtitle}
                  </span>
                )}
              </span>

              {choice.recommended && (
                <span
                  className={cn(
                    "shrink-0 rounded-lg bg-emerald-500",
                    "px-2 py-1",
                    "text-[8px] font-bold uppercase",
                    "tracking-wide text-white",
                    "sm:px-3 sm:text-[9px]",
                  )}
                >
                  <span className="xs:inline hidden">Recommandé</span>

                  <span className="xs:hidden">Recommandé</span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onContinue}
        className={cn(
          "mx-auto mt-4 flex min-h-11 w-full max-w-[280px]",
          "shrink-0 items-center justify-center gap-3 rounded-xl",
          "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500",
          "px-6 text-sm font-bold text-white",
          "shadow-[0_8px_20px_rgba(79,70,229,0.22)]",
          "transition-all duration-200",
          "hover:-translate-y-0.5",
          "hover:shadow-[0_11px_25px_rgba(79,70,229,0.28)]",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
          "sm:mt-5 sm:min-h-12 sm:text-base",
        )}
      >
        Commencer
        <ArrowRightIcon aria-hidden size={19} weight="bold" className="shrink-0" />
      </button>
    </section>
  );
};
